import { useEffect, useState, useCallback } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip as ChartTooltip, ResponsiveContainer, Legend
} from 'recharts';
import { BarChart3, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { useT } from '../i18n';

const CORES_STATUS = {
  enviado: '#16a34a',
  erro: '#b91c1c',
  pendente: '#d97706',
  outros: '#6b7280',
};

export default function Metricas() {
  const { t } = useT();
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState('');
  const [metricas, setMetricas] = useState(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    api.get('/campanha/listar').then(r => setCampanhas(r.data)).catch(() => {});
  }, []);

  const buscar = useCallback(async (id) => {
    if (!id) { setMetricas(null); return; }
    setCarregando(true);
    try {
      const { data } = await api.get(`/envio/campanha/${id}/metricas`);
      setMetricas(data);
    } catch {
      setMetricas(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  function selecionar(id) {
    setCampanhaId(id);
    buscar(id);
  }

  const dadosPizza = metricas?.por_status?.map(s => ({
    name: ['enviado', 'erro', 'pendente'].includes(s.status)
      ? t(`metricas.status_${s.status}`)
      : s.status,
    value: s.total,
    cor: CORES_STATUS[s.status] || CORES_STATUS.outros
  })) || [];

  const dadosHora = metricas?.por_hora?.map(h => ({
    hora: `${String(h.hora).padStart(2, '0')}h`,
    total: h.total
  })) || [];

  const card = {
    background: '#fff',
    border: '1px solid var(--color-divider)',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16
  };

  return (
    <div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      <div className="page-h">
        <div>
          <h1 className="page-h-title">
            <BarChart3 size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {t('metricas.titulo')}
          </h1>
          <p className="page-h-sub">{t('metricas.subtitulo')}</p>
        </div>
        {campanhaId && (
          <div className="page-h-actions">
            <button onClick={() => buscar(campanhaId)} disabled={carregando} className="btn btn-secondary btn-sm">
              <RefreshCw size={13} style={{ animation: carregando ? 'spin 1s linear infinite' : 'none' }} />
              {t('metricas.btn_atualizar')}
            </button>
          </div>
        )}
      </div>

      <div className="field" style={{ maxWidth: 420 }}>
        <label className="label">{t('metricas.selecionar_campanha')}</label>
        <select value={campanhaId} onChange={e => selecionar(e.target.value)} className="select">
          <option value="">{t('metricas.selecione_opcao')}</option>
          {campanhas.map(c => <option key={c.id} value={c.id}>{c.nome} — {c.status}</option>)}
        </select>
      </div>

      {carregando && <div className="empty">{t('metricas.carregando')}</div>}

      {metricas && !carregando && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <KPI label={t('metricas.kpi_total')} valor={metricas.totais.total} />
            <KPI label={t('metricas.kpi_enviados')} valor={metricas.totais.enviados} cor={CORES_STATUS.enviado} />
            <KPI label={t('metricas.kpi_erros')} valor={metricas.totais.erros} cor={CORES_STATUS.erro} />
            <KPI label={t('metricas.kpi_pendentes')} valor={metricas.totais.pendentes} cor={CORES_STATUS.pendente} />
            <KPI label={t('metricas.kpi_taxa')} valor={`${metricas.totais.taxa_entrega}%`} cor={CORES_STATUS.enviado} />
            <KPI label={t('metricas.kpi_tentativas')} valor={metricas.totais.media_tentativas?.toFixed(2) ?? '0.00'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            <div style={card}>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>{t('metricas.grafico_status')}</h3>
              {dadosPizza.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('metricas.sem_dados')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={dadosPizza} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                      {dadosPizza.map((entry, i) => <Cell key={i} fill={entry.cor} />)}
                    </Pie>
                    <Legend />
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={card}>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>{t('metricas.grafico_hora')}</h3>
              {metricas.totais.total === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('metricas.sem_dados')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={dadosHora}>
                    <XAxis dataKey="hora" fontSize={11} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <ChartTooltip />
                    <Bar dataKey="total" fill="#13305f" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div style={{ ...card, marginTop: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>{t('metricas.top_erros_titulo')}</h3>
            {metricas.top_erros.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('metricas.sem_erros')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('metricas.col_erro')}</th>
                    <th style={{ width: 100, textAlign: 'right' }}>{t('metricas.col_ocorrencias')}</th>
                  </tr>
                </thead>
                <tbody>
                  {metricas.top_erros.map((e, i) => (
                    <tr key={i}>
                      <td className="table-cell-muted" style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.erro}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{e.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {campanhaId && !metricas && !carregando && (
        <div className="empty">{t('metricas.sem_dados')}</div>
      )}
    </div>
  );
}

function KPI({ label, valor, cor }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--color-divider)',
      borderRadius: 8, padding: 14
    }}>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: cor || '#101828' }}>{valor}</div>
    </div>
  );
}
